/**
 * Vendors — suppliers the org procures/services assets through. Contact and address fields are
 * plain free text (per the `Vendor` model's own header comment), not the strict internal
 * Employment conventions — e.g. `contactPhone` is NOT validated as E.164 `phoneSchema`, and
 * `country` is a free-text field, not `countrySchema`, since a vendor's registered country isn't
 * gated the way an employee's or legal entity's is.
 *
 * `primaryCategoryId` optionally links a vendor to an `AssetCategory` (asset-configuration.contract.ts)
 * — nullable, since a vendor need not be scoped to one category.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  dateOnlySchema,
  errorBodySchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
  vendorAmcStatusSchema,
} from '../schemas/common.js';

const c = initContract();

/** Synchronous CSV export: config data is bounded, so the file rides back inline as a data URL. */
export const assetCsvExportSchema = z.object({
  fileName: z.string(),
  dataUrl: z.string(),
});

/** Blank optional text arrives from forms as ''; store it as null/omitted, not as an empty value. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const vendorSummarySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  productsServices: z.string().nullable(),
  primaryCategoryId: uuidSchema.nullable(),
  /** Denormalized for display — so a list of vendors needs no per-row category lookup. */
  primaryCategoryName: z.string().nullable(),
  warrantyPlan: z.string().nullable(),
  amcStatus: vendorAmcStatusSchema,
  /** Assets procured from this vendor — the count of `Asset.vendorId` links (AST-26). */
  associatedAssetCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
});
export type VendorSummary = z.infer<typeof vendorSummarySchema>;

export const vendorDetailSchema = vendorSummarySchema.extend({
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  amcExpiryDate: dateOnlySchema.nullable(),
  contractNotes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type VendorDetail = z.infer<typeof vendorDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listVendorsQuerySchema = paginationQuerySchema.extend({
  /** Row ids are uuids; a malformed cursor is a 422 here, not a Prisma error surfacing as a 500. */
  cursor: uuidSchema.optional(),
  /** Matches code, name, or contact name. */
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('all'),
  primaryCategoryId: uuidSchema.optional(),
  amcStatus: vendorAmcStatusSchema.optional(),
});

export const createVendorBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  contactName: optionalText(200),
  contactEmail: z.string().trim().toLowerCase().email().max(320).optional(),
  contactPhone: optionalText(50),
  addressLine1: optionalText(300),
  addressLine2: optionalText(300),
  city: optionalText(100),
  stateCode: optionalText(50),
  postalCode: optionalText(20),
  country: optionalText(100),
  productsServices: optionalText(1000),
  primaryCategoryId: uuidSchema.optional(),
  warrantyPlan: optionalText(200),
  amcStatus: vendorAmcStatusSchema.optional(),
  amcExpiryDate: dateOnlySchema.optional(),
  contractNotes: optionalText(2000),
});

/** Every field optional; `code` is immutable once created, matching the other config entities. */
export const updateVendorBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  contactName: z.string().trim().max(200).nullable().optional(),
  contactEmail: z.string().trim().toLowerCase().email().max(320).nullable().optional(),
  contactPhone: z.string().trim().max(50).nullable().optional(),
  addressLine1: z.string().trim().max(300).nullable().optional(),
  addressLine2: z.string().trim().max(300).nullable().optional(),
  city: z.string().trim().max(100).nullable().optional(),
  stateCode: z.string().trim().max(50).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  country: z.string().trim().max(100).nullable().optional(),
  productsServices: z.string().trim().max(1000).nullable().optional(),
  primaryCategoryId: uuidSchema.nullable().optional(),
  warrantyPlan: z.string().trim().max(200).nullable().optional(),
  amcStatus: vendorAmcStatusSchema.optional(),
  amcExpiryDate: dateOnlySchema.nullable().optional(),
  contractNotes: z.string().trim().max(2000).nullable().optional(),
  isActive: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const vendorContract = c.router(
  {
    listVendors: {
      method: 'GET',
      path: '/',
      query: listVendorsQuerySchema,
      responses: {
        200: paginatedSchema(vendorSummarySchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List vendors',
      metadata: routeMeta('asset.vendor:read'),
    },
    createVendor: {
      method: 'POST',
      path: '/',
      body: createVendorBodySchema,
      responses: {
        201: vendorDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a vendor',
      metadata: routeMeta('asset.vendor:manage'),
    },
    // Declared BEFORE getVendor: `/export` would otherwise match `/:vendorId` as an id.
    exportVendors: {
      method: 'GET',
      path: '/export',
      responses: {
        200: assetCsvExportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Export vendors as CSV (asset:export)',
      metadata: routeMeta(['asset:export', 'asset.vendor:read']),
    },
    getVendor: {
      method: 'GET',
      path: '/:vendorId',
      responses: {
        200: vendorDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get a vendor by id',
      metadata: routeMeta('asset.vendor:read'),
    },
    updateVendor: {
      method: 'PATCH',
      path: '/:vendorId',
      body: updateVendorBodySchema,
      responses: {
        200: vendorDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a vendor',
      metadata: routeMeta('asset.vendor:manage'),
    },
  },
  { pathPrefix: '/asset-management/vendors' },
);
